import { chartGroupByOption, chartSortBy, propertyType } from 'shared/constants.json';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { isDateGrouping } from '@/composables/grid-page/chart/utils';
import { shallowCopy } from 'shared/lib/copy';
import {
  sortAlphaNumeric,
  sortByAttr,
  sortDatetimeInterval,
} from 'shared/lib/sort';

export default function useChartSort() {
  const getType = (groupBy) => {
    if (groupBy.groupBy === chartGroupByOption.none) {
      return '';
    }
    if (groupBy.groupBy === chartGroupByOption.bucket) {
      return 'bucket';
    }
    if (isDateGrouping(groupBy.groupBy)) {
      return 'dateGrouping';
    }
    if (groupBy.isProperty === true) {
      return groupBy.property?.type ?? '';
    }
    if (groupBy.edge === goalConfig.edges.goalCycle) {
      return 'datetimeInterval';
    }
    return groupBy.edgeType;
  };

  // Extracts the leading number from a bucket label for numeric sorting.
  // "0 - 20" → 0, "20 - 40" → 20, "> 100" → Infinity, "< 0" → -Infinity
  const bucketSortValue = (label) => {
    if (typeof label !== 'string') return 0;
    if (label.startsWith('>')) return Infinity;
    if (label.startsWith('<')) return -Infinity;
    const num = parseFloat(label);
    return Number.isNaN(num) ? 0 : num;
  };

  const sortBucket = (a, b) => bucketSortValue(a.originalValue) - bucketSortValue(b.originalValue);

  // TODO: If we resolve cached properties, we need to extend this
  // to be able to sort by uid order.
  const getSorter = (groupBy) => {
    switch (getType(groupBy)) {
      case 'datetimeInterval':
        return sortDatetimeInterval('start', 'end');
      case 'dateGrouping':
        return sortByAttr('originalValue', sortAlphaNumeric);
      case 'bucket':
        return sortBucket;
      default:
        return sortByAttr('title', sortAlphaNumeric);
    }
  };

  const isDescending = (sortByValue) => sortByValue === chartSortBy.valueDesc || sortByValue === chartSortBy.countDesc;

  const isCountSort = (sortByValue) => sortByValue === chartSortBy.countAsc || sortByValue === chartSortBy.countDesc;

  const isConfiguredSort = (sortByValue) => sortByValue != null;

  const defaultSortByValue = (groupBy) => (
    groupBy && getType(groupBy) === propertyType.status ? chartSortBy.manual : chartSortBy.valueAsc
  );

  // Automatic sort: used before fillMissingCategories to establish
  // natural ordering (chronological for dates, alphabetical otherwise).
  const sort = (groups, groupBy) => {
    const sorted = shallowCopy(groups);
    sorted.sort(getSorter(groupBy));
    return sorted;
  };

  const sortByManualOrder = (groups, manualOrder) => {
    if (!Array.isArray(manualOrder) || manualOrder.length === 0) {
      return groups;
    }
    const orderMap = new Map(manualOrder.map((val, i) => [String(val), i]));
    const inOrder = groups.filter((g) => orderMap.has(String(g.originalValue)));
    const rest = groups.filter((g) => !orderMap.has(String(g.originalValue)));
    inOrder.sort((a, b) => orderMap.get(String(a.originalValue)) - orderMap.get(String(b.originalValue)));
    return [...inOrder, ...rest];
  };

  const countSorter = (groupedResult, aggregator) => (a, b) => (
    (groupedResult[a.originalValue]?.[aggregator] ?? 0) - (groupedResult[b.originalValue]?.[aggregator] ?? 0)
  );

  // User-configured sort: applied after fillMissingCategories.
  // When unconfigured, status groupings default to manual (following the
  // property's option order); everything else defaults to valueAsc.
  const sortByConfig = (groups, groupBy, groupedResult = null, aggregator = null) => {
    const sortByValue = isConfiguredSort(groupBy.sortBy) ? groupBy.sortBy : defaultSortByValue(groupBy);

    if (sortByValue === chartSortBy.manual) {
      const order = Array.isArray(groupBy.manualOrder) ? groupBy.manualOrder : [];
      return sortByManualOrder(shallowCopy(groups), order);
    }

    const useCountSort = isCountSort(sortByValue) && groupedResult !== null && aggregator !== null;
    const sorter = useCountSort ? countSorter(groupedResult, aggregator) : getSorter(groupBy);

    const sorted = shallowCopy(groups);
    sorted.sort(sorter);
    if (isDescending(sortByValue)) {
      sorted.reverse();
    }
    return sorted;
  };

  return { sort, sortByConfig, defaultSortByValue };
}
